/**
 * Pullman Bookings — "Export to Excel".
 *
 * Writes whatever the active report tab is currently showing (its own columns,
 * after the search / service / overs-unders filters) to a .xlsx workbook:
 *
 *   Sheet 1 — the table, header row frozen, numbers written as real numbers
 *             so the catering teams can sort, filter and pivot in Excel.
 *   Sheet 2 — "About": which report, the date window and which filters were
 *             applied, so a file sitting in someone's Downloads folder still
 *             says what it is.
 *
 * Uses write-excel-file, the same library as the Retail & Tea Rooms export.
 * Structural types only — nothing from BookingsDashboard needs exporting.
 */
import writeXlsxFile from "write-excel-file/browser";

type Row = Record<string, unknown>;

export interface XlsxColumnDef {
  key: string;
  label: string;
  align?: "right" | "center";
  fmt?: (v: unknown) => string;
}

export interface XlsxReportDef {
  key: string;
  label: string;
  columns: XlsxColumnDef[];
  hasTotals?: boolean;
}

export interface XlsxExportContext {
  from: string;
  to: string;
  search?: string;
  serviceLabel?: string | null;
  overShortOnly?: boolean;
  /** The counting variant the rows were produced under ("As MERAC reports (default)" / "One count per booking"). */
  countingLabel?: string;
}

interface Cell {
  value?: string | number | null;
  type?: StringConstructor | NumberConstructor;
  format?: string;
  fontWeight?: "bold";
  align?: "right" | "center";
  backgroundColor?: string;
  color?: string;
}

const MONEY_FMT = "£#,##0.00";
const INT_FMT = "#,##0";

// Columns that hold money. Everything else right-aligned and numeric is treated
// as a count; Booking / Cust No are identifiers, so they stay unformatted
// integers (no thousands separator on a booking ref).
const MONEY_KEYS = new Set(["Price"]);
const ID_KEYS = new Set(["Booking", "AccountNo"]);

function asNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const t = v.trim();
    if (t === "") return null;
    // Tolerate values that arrive pre-formatted (e.g. "£12.50", "1,234").
    const n = Number(t.replace(/[£,\s]/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** One body cell — numeric where the column is genuinely numeric, else text. */
function bodyCell(col: XlsxColumnDef, raw: unknown): Cell {
  if (raw == null || raw === "") return { value: null };

  const numeric = col.align === "right" ? asNumber(raw) : null;
  if (numeric != null) {
    if (MONEY_KEYS.has(col.key)) return { value: numeric, type: Number, format: MONEY_FMT };
    if (ID_KEYS.has(col.key)) return { value: numeric, type: Number };
    return { value: numeric, type: Number, format: Number.isInteger(numeric) ? INT_FMT : undefined };
  }

  const text = col.fmt ? col.fmt(raw) : String(raw);
  return { value: text, type: String, align: col.align };
}

function headerRow(columns: XlsxColumnDef[]): Cell[] {
  return columns.map((c) => ({
    value: c.label,
    type: String,
    fontWeight: "bold" as const,
    backgroundColor: "#0B3D2E",
    color: "#FFFFFF",
    align: c.align,
  }));
}

/**
 * Totals across the EXPORTED rows (not the report-wide totals the grid shows),
 * so the sheet adds up to itself.
 */
function totalsRow(columns: XlsxColumnDef[], rows: Row[]): Cell[] | null {
  const sumKeys = ["Courses", "Covers", "Price"].filter((k) => columns.some((c) => c.key === k));
  if (sumKeys.length === 0) return null;

  const sums = new Map<string, number>();
  for (const k of sumKeys) {
    let total = 0;
    for (const r of rows) total += asNumber(r[k]) ?? 0;
    sums.set(k, total);
  }

  return columns.map((c, i) => {
    if (i === 0) return { value: `Totals (${rows.length} exported rows)`, type: String, fontWeight: "bold" as const };
    if (!sums.has(c.key)) return { value: null };
    const v = sums.get(c.key)!;
    return {
      value: v,
      type: Number,
      format: MONEY_KEYS.has(c.key) ? MONEY_FMT : INT_FMT,
      fontWeight: "bold" as const,
    };
  });
}

function aboutSheet(def: XlsxReportDef, rowCount: number, ctx: XlsxExportContext): Cell[][] {
  const filters: string[] = [];
  if (ctx.serviceLabel) filters.push(`Service: ${ctx.serviceLabel}`);
  if (ctx.search?.trim()) filters.push(`Search: "${ctx.search.trim()}"`);
  if (ctx.overShortOnly) filters.push("Overs/Unders only");

  const pair = (label: string, value: string): Cell[] => [
    { value: label, type: String, fontWeight: "bold" },
    { value: value, type: String },
  ];

  return [
    [{ value: "Pullman Bookings export", type: String, fontWeight: "bold" }, { value: null }],
    pair("Report", def.label),
    pair("Date range", `${ctx.from} to ${ctx.to}`),
    pair("Rows exported", String(rowCount)),
    pair("Filters", filters.length ? filters.join("  ·  ") : "None"),
    pair("Counting", ctx.countingLabel ?? "As MERAC reports (default)"),
    pair("Generated", new Date().toLocaleString("en-GB")),
    [{ value: null }, { value: null }],
    [
      {
        value:
          "Totals on the data sheet are summed across the rows in this file only. " +
          "The report-wide totals shown on screen cover the whole date range before filtering.",
        type: String,
      },
      { value: null },
    ],
  ];
}

/** Excel sheet names: 31 chars max, and []:*?/\ are illegal. */
function safeSheetName(label: string): string {
  return (label || "Data").replace(/[[\]:*?/\\]/g, " ").trim().slice(0, 31) || "Data";
}

function widthFor(col: XlsxColumnDef): number {
  if (col.key === "Notes" || col.key === "Item") return 42;
  if (col.key === "AccName" || col.key === "Journey" || col.key === "TicketTypes") return 26;
  if (col.key === "Seats" || col.key === "Carriage") return 18;
  return Math.max(10, Math.min(24, col.label.length + 4));
}

/** The workbook, as plain data. Split out from the download so it can be tested. */
export function buildBookingsSheets(def: XlsxReportDef, rows: Row[], ctx: XlsxExportContext) {
  const columns = def.columns;
  const data: Cell[][] = [headerRow(columns), ...rows.map((r) => columns.map((c) => bodyCell(c, r[c.key])))];

  if (def.hasTotals) {
    const t = totalsRow(columns, rows);
    if (t) data.push(t);
  }

  return [
    {
      data,
      sheet: safeSheetName(def.label),
      columns: columns.map((c) => ({ width: widthFor(c) })),
      stickyRowsCount: 1,
    },
    { data: aboutSheet(def, rows.length, ctx), sheet: "About", columns: [{ width: 18 }, { width: 70 }] },
  ];
}

export async function downloadBookingsXlsx(
  def: XlsxReportDef,
  rows: Row[],
  ctx: XlsxExportContext
): Promise<void> {
  const sheets = buildBookingsSheets(def, rows, ctx);

  const blob = await writeXlsxFile(sheets as never).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `pullman-bookings-${def.key}-${ctx.from}_to_${ctx.to}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
